import { Instagram, Music2, Linkedin, Check, Plane, Star, Crown } from 'lucide-react';
interface UpgradeConfirmationEmailProps {
  userName?: string;
  purchaseType: 'boost' | 'subscription';
  tier: 'premium_economy' | 'first_class';
  billingPeriod?: 'monthly' | 'annual'; // Only for subscriptions
  boostDuration?: '3' | '7' | '14'; // Only for boost passes
  price: string;
  activationDate: string;
}
const UpgradeConfirmationEmail = ({
  userName = 'Voyager',
  purchaseType,
  tier,
  billingPeriod,
  boostDuration,
  price,
  activationDate
}: UpgradeConfirmationEmailProps) => {
  const isBoost = purchaseType === 'boost';
  const isPremiumEconomy = tier === 'premium_economy';
  const isFirstClass = tier === 'first_class';
  const tierName = isPremiumEconomy ? 'Premium Economy Pack' : 'First Class Pack';
  const tierIcon = isPremiumEconomy ? Star : Crown;
  const tierColor = isPremiumEconomy ? '#1a9a9a' : '#f0d763';
  const tierBgGradient = isPremiumEconomy ? 'linear-gradient(135deg, #1a9a9a, #6dd4d4)' : 'linear-gradient(135deg, #f0d763, #f8e89f)';
  const purchaseTitle = isBoost ? `${boostDuration}-Day ${tierName} Boost Pass` : `${tierName} ${billingPeriod === 'annual' ? 'Annual' : 'Monthly'} Subscription`;
  const features = isPremiumEconomy ? ['Unlimited Searches To All Features', 'AI-Powered Personalized Lists', 'Visa Requirements Check', 'Local Destination Tips', 'Health & Wellness Preparation', 'Jet Lag Minimizer', 'Smart Currency Converter', 'Group Expense Sharing', 'Early Access To New Features'] : ['Everything In Premium Economy', 'Offline Mode - Access Lists Without Internet', 'Multi-City Packing Lists', 'Custom Travel Templates - Save & Reuse Lists', 'Advanced Travel Analytics - Spending & Travel Insights', 'Group Packing Progress Sharing'];
  const Icon = tierIcon;
  return <>
    <style>{`
      @media (max-width: 640px) {
        .email-container { padding: 20px 12px !important; }
        .email-logo { width: 180px !important; }
        .email-heading { font-size: 24px !important; margin: 8px 0 16px !important; }
        .email-text { font-size: 14px !important; line-height: 22px !important; }
        .email-summary-box { padding: 20px 16px !important; margin: 24px 0 !important; }
        .email-summary-title { font-size: 20px !important; }
        .email-icon-wrapper { padding: 6px !important; }
        .email-button { padding: 12px 24px !important; font-size: 14px !important; }
        .email-feature-item { font-size: 13px !important; line-height: 20px !important; margin-bottom: 8px !important; }
        .email-social-icon { width: 24px !important; height: 24px !important; }
      }
    `}</style>
    <div style={{
    backgroundColor: '#f0fafa',
    minHeight: '100vh',
    padding: '40px 20px',
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
      }}>
        <div className="email-container" style={{
        backgroundColor: '#ffffff',
        margin: '0 auto',
        padding: '40px 20px',
      borderRadius: '8px',
      maxWidth: '600px'
    }}>
        <div style={{
        textAlign: 'center',
        marginBottom: '0'
      }}>
          <img src="/lovable-uploads/odyvoy-logo.png" alt="OdyVoy Logo" className="email-logo" style={{
          width: '250px',
          height: 'auto',
          margin: '0 auto'
        }} />
        </div>

        <h1 className="email-heading" style={{
        color: tierColor,
        fontSize: '32px',
        fontWeight: 'bold',
        margin: '8px 0 24px',
        padding: '0',
        textAlign: 'center'
      }}>Welcome to {tierName}! 🎉
        </h1>
        
        <p className="email-text" style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        margin: '16px 0',
        textAlign: 'center'
      }}>Hi {userName},
        </p>
        
        <p className="email-text" style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        margin: '16px 0',
        textAlign: 'center'
      }}>
          Thank you for upgrading! Your {isBoost ? 'boost pass' : 'subscription'} is now active and ready to make your travels even more amazing.
        </p>

        {/* Purchase Summary Box */}
        <div className="email-summary-box" style={{
        background: tierBgGradient,
        borderRadius: '8px',
        padding: '24px',
        margin: '32px 0',
        textAlign: 'center'
      }}>
          <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
            <div className="email-icon-wrapper" style={{
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.2)'
          }}>
              <Icon size={32} color="#ffffff" />
            </div>
            <h2 className="email-summary-title" style={{
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            padding: '0'
          }}>
              {purchaseTitle}
            </h2>
          </div>
          
          <div style={{
          backgroundColor: 'rgba(255,255,255,0.9)',
          borderRadius: '6px',
          padding: '16px',
          margin: '16px auto',
          maxWidth: '400px'
        }}>
            <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
              <span style={{
              color: '#333',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>Amount Paid:</span>
              <span style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>{price}</span>
            </div>
            <div style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}>
              <span style={{
              color: '#333',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>Activated On:</span>
              <span style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>{activationDate}</span>
            </div>
          </div>
          
          {isBoost ? <p style={{
          color: '#ffffff',
          fontSize: '14px',
          margin: '16px 0 0',
          opacity: '0.95'
        }}>
              Your {boostDuration}-day access expires on {new Date(new Date(activationDate).getTime() + parseInt(boostDuration) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
            </p> : <p style={{
          color: '#ffffff',
          fontSize: '14px',
          margin: '16px 0 0',
          opacity: '0.95'
        }}>{billingPeriod === 'annual' ? 'Renews Annually • Cancel Anytime' : 'Renews Monthly • Cancel Anytime'}</p>}
        </div>

        {/* What's Included Section */}
        <div style={{
        margin: '32px 0'
      }}>
          <h3 style={{
          color: '#1a9a9a',
          fontSize: '22px',
          fontWeight: 'bold',
          margin: '0 0 20px',
          textAlign: 'center'
        }}>
            What's Included
          </h3>
          
          <div style={{
          backgroundColor: '#f0fafa',
          borderRadius: '8px',
          padding: '24px'
        }}>
            {features.map((feature, index) => <div key={index} className="email-feature-item" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: index === features.length - 1 ? '0' : '12px'
          }}>
                <div style={{
              flexShrink: 0,
              paddingTop: '2px'
            }}>
                  <Check size={20} color="#1a9a9a" strokeWidth={2.5} />
                </div>
                <span style={{
              color: '#333',
              fontSize: '15px',
              lineHeight: '22px'
            }}>{feature}</span>
              </div>)}
          </div>
        </div>

        {/* Getting Started CTA */}
        <div style={{
        textAlign: 'center',
        margin: '32px 0'
      }}>
          <a href="/dashboard" className="email-button" style={{
          backgroundColor: tierColor,
          borderRadius: '6px',
          color: isPremiumEconomy ? '#ffffff' : '#1a9a9a',
          fontSize: '16px',
          fontWeight: 'bold',
          textDecoration: 'none',
          textAlign: 'center',
          display: 'inline-block',
          padding: '14px 32px',
          margin: '0'
        }}>
            Start Exploring Now
          </a>
        </div>

        {/* Upgrade Info (only for boost passes) */}
        {isBoost && <div style={{
        backgroundColor: '#fff9e6',
        borderRadius: '8px',
        padding: '20px',
        margin: '24px 0'
      }}>
            <p style={{
          color: '#333',
          fontSize: '15px',
          lineHeight: '22px',
          margin: '0 0 12px'
        }}>
              <strong>Love Your Boost Pass?</strong>
            </p>
            <p style={{
          color: '#666',
          fontSize: '14px',
          lineHeight: '20px',
          margin: '0 0 12px'
        }}>
              Upgrade to a full subscription anytime and get continuous access to all premium features without interruption.
            </p>
            <a href="/pricing" style={{
          color: '#1a9a9a',
          fontSize: '14px',
          fontWeight: 'bold',
          textDecoration: 'underline'
        }}>
              View Subscription Plans →
            </a>
          </div>}

        {/* Support Section */}
        <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '20px',
        margin: '24px 0'
      }}>
          <p style={{
          color: '#333',
          fontSize: '15px',
          lineHeight: '22px',
          margin: '0 0 12px'
        }}>
            <strong>Need Help Getting Started?</strong>
          </p>
          <p style={{
          color: '#666',
          fontSize: '14px',
          lineHeight: '20px',
          margin: '0 0 12px'
        }}>
            Our team is here to help you make the most of your upgrade. Check out our help center or reach out to support anytime.
          </p>
          <a href="/help" style={{
          color: '#1a9a9a',
          fontSize: '14px',
          fontWeight: 'bold',
          textDecoration: 'underline'
        }}>
            Visit Help Center →
          </a>
        </div>

        <p style={{
        color: '#333',
        fontSize: '16px',
        lineHeight: '24px',
        marginTop: '32px'
      }}>
          Happy travels,
          <br />
          The OdyVoy Team 🌟
        </p>

        <hr style={{
        borderColor: '#e6e6e6',
        margin: '32px 0'
      }} />

        <div style={{
        textAlign: 'center',
        margin: '32px 0'
      }}>
          <p style={{
          color: '#333',
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '16px'
        }}>
            Stay Connected With Us
          </p>
          <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
            <a href="https://www.instagram.com/odyvoy_/" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
            }} aria-label="Follow us on Instagram">
              <Instagram className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.tiktok.com/@odyvoy_" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
            }} aria-label="Follow us on TikTok">
              <Music2 className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.linkedin.com/company/odyvoy/about/?viewAsMember=true" style={{
            textDecoration: 'none',
            color: '#64748b',
            transition: 'color 0.2s'
            }} aria-label="Follow us on LinkedIn">
              <Linkedin className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
          </div>
          
        </div>

        <div style={{
        textAlign: 'center',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #e6e6e6'
      }}>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '8px 0'
        }}>
            <strong>OdyVoy</strong>
          </p>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '8px 0'
        }}>Your AI-Powered Travel Companion</p>
          <p style={{
          color: '#8898aa',
          fontSize: '12px',
          lineHeight: '18px',
          margin: '16px 0'
        }}>
            <a href="/unsubscribe" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Unsubscribe</a>
            {' | '}
            <a href="/privacy" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Privacy Policy</a>
            {' | '}
            <a href="/terms" style={{
            color: '#1a9a9a',
            textDecoration: 'underline'
          }}>Terms of Service</a>
          </p>
          <p style={{
          color: '#8898aa',
          fontSize: '11px',
          lineHeight: '16px',
          margin: '16px 0'
        }}>
            © {new Date().getFullYear()} OdyVoy. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </>;
};
export default UpgradeConfirmationEmail;